// Subgraph ID mappings for different chains
type KeychainList = {
  [key: string]: {
    [chainId: string]: string;
  };
};

const SUBGRAPH_IDS: KeychainList = {
  YEETER: {
    "0xaa36a7": `8Syem3ZN88cut1wL8AqPHNo658Px7M2CkRuHAGuxvf6j`,
    "0x64": `EGG5xEkiKKtGa9frTfBSmL2w7ZmzPDke5ZuvxDRwQcGe`,
    "0xa": `55wEbRchfvjtWsy5NqLc4hp9C7xbX9yk8bAr3UQA8F7x`,
    "0xa4b1": `BeGugH1TsMspZ7Nov1Uq2PQ98X78sqjuEy1JFGLyNgt5`,
    "0x2105": `6vyAqRpCyrhLsfd6TfYAssvKywKhxJykkDbPxJZ4ZcEr`,
  },
  DAOHAUS: {
    "0xaa36a7": `3k93SNY5Y1r4YYWEuPY9mpCm2wnGoYDKRtk82QZJ3Kvw`,
    "0x64": `6x9FK3iuhVFaH9sZ39m8bKB5eckax8sjxooBPNKWWK8r`,
    "0x89": `A4su27JYXR5TkPZmiFHzzqMJnmYttfU3FyrdNBDnnu8T`,
    "0xa": `CgH5vtz9CJPdcSmD3XEh8fCVDjQjnRwrSawg71T1ySXW`,
    "0xa4b1": `GPACxuMWrrPSEJpFqupnePJNMfuArpFabrXLnWvXU2bp`,
    "0x2105": `7yh4eHJ4qpHEiLPAk9BXhL5YgYrTrRE6gWy8x4oHyAqW`,
  },
};

function toNormalizedHexChainId(chainid: string): string {
  if (!chainid) return "";

  const normalizedChainId = chainid.toLowerCase();
  if (!normalizedChainId.startsWith("0x")) {
    const parsedChainId = parseInt(normalizedChainId, 10);
    if (Number.isNaN(parsedChainId)) return "";
    return `0x${parsedChainId.toString(16)}`;
  }

  const parsedChainId = parseInt(normalizedChainId, 16);
  if (Number.isNaN(parsedChainId)) return "";

  return `0x${parsedChainId.toString(16)}`;
}

export function isSupportedSubgraphChain({
  chainid,
  subgraphKey,
}: {
  chainid: string;
  subgraphKey: string;
}): boolean {
  const normalizedHexChainId = toNormalizedHexChainId(chainid);
  if (!normalizedHexChainId) return false;

  return Boolean(SUBGRAPH_IDS[subgraphKey]?.[normalizedHexChainId]);
}

// Graph URL builder for DAO Haus subgraphs
export const getGraphUrl = ({
  chainid,
  graphKey,
  subgraphKey,
}: {
  chainid: string;
  graphKey: string;
  subgraphKey: string;
}): string => {
  if (!chainid || !graphKey || !subgraphKey) {
    throw new Error("Missing required parameters for Graph URL");
  }

  // Convert chainid to hex format if it's decimal
  const hexChainId = toNormalizedHexChainId(chainid);
  if (!hexChainId) {
    throw new Error(`Unsupported chain ID: ${chainid} or subgraph key: ${subgraphKey}`);
  }

  // Get the subgraph hash for the specific chain and subgraph key
  const subgraphHash = SUBGRAPH_IDS[subgraphKey]?.[hexChainId];

  if (!subgraphHash) {
    throw new Error(`Unsupported chain ID: ${chainid} or subgraph key: ${subgraphKey}`);
  }

  // Base URL for DAO Haus subgraphs
  const baseUrl = "https://gateway-arbitrum.network.thegraph.com/api";

  return `${baseUrl}/${graphKey}/subgraphs/id/${subgraphHash}`;
};

// Token indexer URL builder for Sequence
export const getTokenIndexerUrl = ({
  chainid,
}: {
  chainid: string;
}): string => {
  if (!chainid) {
    throw new Error("Missing chainid for token indexer URL");
  }

  // Sequence indexer URLs by chain
  const indexerUrls: Record<string, string> = {
    "1": "https://ethereum-indexer.sequence.app",
    "137": "https://polygon-indexer.sequence.app",
    "42161": "https://arbitrum-indexer.sequence.app",
    "10": "https://optimism-indexer.sequence.app",
    "8453": "https://base-indexer.sequence.app",
    "56": "https://bsc-indexer.sequence.app",
    "43114": "https://avalanche-indexer.sequence.app",
    "250": "https://fantom-indexer.sequence.app",
    "25": "https://cronos-indexer.sequence.app",
    "100": "https://gnosis-indexer.sequence.app",
    "1284": "https://moonbeam-indexer.sequence.app",
    "1285": "https://moonriver-indexer.sequence.app",
    "592": "https://astar-indexer.sequence.app",
    "1088": "https://metis-indexer.sequence.app",
    "288": "https://boba-indexer.sequence.app",
    "1666600000": "https://harmony-indexer.sequence.app",
    "128": "https://heco-indexer.sequence.app",
    "66": "https://okc-indexer.sequence.app",
    "8217": "https://klaytn-indexer.sequence.app",
    "106": "https://velas-indexer.sequence.app",
    "40": "https://telos-indexer.sequence.app",
    "1287": "https://moonbase-indexer.sequence.app",
    "80001": "https://mumbai-indexer.sequence.app",
    "5": "https://goerli-indexer.sequence.app",
    "11155111": "https://sepolia-indexer.sequence.app",
    "97": "https://bsc-testnet-indexer.sequence.app",
    "43113": "https://fuji-indexer.sequence.app",
    "4002": "https://fantom-testnet-indexer.sequence.app",
    "338": "https://cronos-testnet-indexer.sequence.app",
    "10200": "https://gnosis-testnet-indexer.sequence.app",
    "81": "https://astar-testnet-indexer.sequence.app", // Shibuya
    "599": "https://metis-testnet-indexer.sequence.app",
    "28882": "https://boba-testnet-indexer.sequence.app",
    "1666700000": "https://harmony-testnet-indexer.sequence.app",
    "256": "https://heco-testnet-indexer.sequence.app",
    "65": "https://okc-testnet-indexer.sequence.app",
    "1001": "https://klaytn-testnet-indexer.sequence.app", // Baobab
    "111": "https://velas-testnet-indexer.sequence.app", // Velas Testnet
    "41": "https://telos-testnet-indexer.sequence.app", // Telos Testnet
  };

  const indexerUrl = indexerUrls[chainid];

  if (!indexerUrl) {
    throw new Error(`Unsupported chain ID: ${chainid}`);
  }

  return indexerUrl;
};

export function getAlchemyRpcKey(): string | undefined {
  return process.env.NEXT_PUBLIC_ALCHEMY_API_KEY || undefined
}

/** Ordered RPC URLs for a chain — primary first, then fallbacks for transient failures. */
export function getRpcUrls({
  chainid,
  rpcKey,
}: {
  chainid: string
  rpcKey?: string
}): string[] {
  const key = rpcKey ?? getAlchemyRpcKey()
  const urls: string[] = []

  const add = (url: string | null | undefined) => {
    if (url && !urls.includes(url)) urls.push(url)
  }

  add(getRpcUrl({ chainid, rpcKey: key }))

  if (chainid === "8453") {
    add(process.env.NEXT_PUBLIC_BASE_RPC_URL || process.env.BASE_RPC_URL)
    add("https://mainnet.base.org")
    add("https://base.publicnode.com")
  }

  return urls
}

// RPC URL builder for different chains
export const getRpcUrl = ({
  chainid,
  rpcKey,
}: {
  chainid: string;
  rpcKey?: string;
}): string => {
  if (!chainid) {
    throw new Error("Missing chainid for RPC URL");
  }

  // Prefer Alchemy when a key is provided; otherwise use public CORS-safe RPCs.
  // Never fall back to Alchemy "demo" — it fails browser CORS and retries forever.
  const alchemyUrl = (host: string) =>
    rpcKey ? `https://${host}.g.alchemy.com/v2/${rpcKey}` : null

  const baseRpcOverride =
    process.env.NEXT_PUBLIC_BASE_RPC_URL || process.env.BASE_RPC_URL || null

  const rpcUrls: Record<string, string> = {
    "1": alchemyUrl("eth-mainnet") || "https://ethereum.publicnode.com",
    "42161": alchemyUrl("arb-mainnet") || "https://arb1.arbitrum.io/rpc",
    "10": alchemyUrl("opt-mainnet") || "https://mainnet.optimism.io",
    "8453":
      baseRpcOverride ||
      alchemyUrl("base-mainnet") ||
      "https://mainnet.base.org",
    "56": "https://bsc-dataseed.binance.org/",
    "43114": "https://api.avax.network/ext/bc/C/rpc",
    "250": "https://rpc.ftm.tools/",
    "25": "https://evm-cronos.crypto.org",
    "100": "https://rpc.gnosischain.com",
    "1284": "https://rpc.api.moonbeam.network",
    "1285": "https://rpc.api.moonriver.moonbeam.network",
    "592": "https://evm.astar.network",
    "1088": "https://andromeda.metis.io/?owner=1088",
    "288": "https://mainnet.boba.network",
    "1666600000": "https://api.harmony.one",
    "128": "https://http-mainnet.hecochain.com",
    "66": "https://exchainrpc.okex.org",
    "8217": "https://public-node-api.klaytnapi.com/v1/cypress",
    "106": "https://evmexplorer.velas.com/rpc",
    "40": "https://mainnet.telos.net/evm",
    "1287": "https://rpc.api.moonbase.moonbeam.network",
    "5": alchemyUrl("eth-goerli") || "https://rpc.ankr.com/eth_goerli",
    "11155111": alchemyUrl("eth-sepolia") || "https://rpc.sepolia.org",
    "97": "https://data-seed-prebsc-1-s1.binance.org:8545/",
    "43113": "https://api.avax-test.network/ext/bc/C/rpc",
    "4002": "https://rpc.testnet.fantom.network/",
    "338": "https://cronos-testnet-3.crypto.org:8545",
    "10200": "https://rpc.chiadochain.net",
    "28882": "https://testnet.boba.network",
    "1666700000": "https://api.s0.b.hmny.io",
    "256": "https://http-testnet.hecochain.com",
    "65": "https://exchaintestrpc.okex.org",
    "1001": "https://api.baobab.klaytn.net:8651",
    "111": "https://evmexplorer.testnet.velas.com/rpc",
    "41": "https://testnet.telos.net/evm",
  };

  const rpcUrl = rpcUrls[chainid];

  if (!rpcUrl) {
    throw new Error(`Unsupported chain ID: ${chainid}`);
  }

  return rpcUrl;
};

// Block explorer URL builder
export const getBlockExplorerUrl = ({
  chainid,
  address,
  txHash,
}: {
  chainid: string;
  address?: string;
  txHash?: string;
}): string => {
  if (!chainid) {
    throw new Error("Missing chainid for block explorer URL");
  }

  const explorerUrls: Record<string, string> = {
    "1": "https://etherscan.io",
    "137": "https://polygonscan.com",
    "42161": "https://arbiscan.io",
    "10": "https://optimistic.etherscan.io",
    "8453": "https://basescan.org",
    "56": "https://bscscan.com",
    "43114": "https://snowtrace.io",
    "250": "https://ftmscan.com",
    "25": "https://cronoscan.com",
    "100": "https://gnosisscan.io",
    "1284": "https://moonscan.io",
    "1285": "https://moonriver.moonscan.io",
    "592": "https://astar.subscan.io",
    "1088": "https://andromeda-explorer.metis.io",
    "288": "https://bobascan.com",
    "1666600000": "https://explorer.harmony.one",
    "128": "https://hecoinfo.com",
    "66": "https://www.oklink.com/en/okc",
    "8217": "https://scope.klaytn.com",
    "106": "https://evmexplorer.velas.com",
    "40": "https://teloscan.io",
    "1287": "https://moonbase.moonscan.io",
    "80001": "https://mumbai.polygonscan.com",
    "5": "https://goerli.etherscan.io",
    "11155111": "https://sepolia.etherscan.io",
    "97": "https://testnet.bscscan.com",
    "43113": "https://testnet.snowtrace.io",
    "4002": "https://testnet.ftmscan.com",
    "338": "https://testnet.cronoscan.com",
    "10200": "https://gnosis-chiado.blockscout.com",
    "28882": "https://testnet.bobascan.com",
    "1666700000": "https://explorer.pops.one",
    "256": "https://testnet.hecoinfo.com",
    "65": "https://www.oklink.com/en/okc-test",
    "1001": "https://baobab.scope.klaytn.com",
    "111": "https://evmexplorer.testnet.velas.com",
    "41": "https://testnet.teloscan.io",
  };

  const baseUrl = explorerUrls[chainid];

  if (!baseUrl) {
    throw new Error(`Unsupported chain ID: ${chainid}`);
  }

  if (txHash) {
    return `${baseUrl}/tx/${txHash}`;
  }

  if (address) {
    return `${baseUrl}/address/${address}`;
  }

  return baseUrl;
};